// src/app/assistant/[assistantId]/page.tsx 
'use client';

import { useEffect, useState, useMemo, useRef } from 'react';
import { notFound } from 'next/navigation';
import FirebaseService, { Assistant } from '@/lib/firebase'; 
import SharePage from '@/components/SharePage';
import type { ConfigProps } from '@/types';
import Aurora from '@/components/Aurora';

const ErrorPage = ({ message }: { message: string }) => (
  <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
      <h1>Error</h1>
      <p>{message}</p>
  </div>
);

  // In your frontend initialization code
  const warmBackend = async () => {
    try {
      await fetch('/api/proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'warmup' })
      });
      //console.log('Backend warmed up');
    } catch (e) {
      console.warn('Backend warmup failed:', e);
    }
  };

function SharePageLoading({
  isExiting = false,
  assistant
}: {
  isExiting?: boolean;
  assistant?: Assistant | null;
}) {
  // Call this on page load
  warmBackend();

  // Use state to avoid hydration mismatch
  const [displayData, setDisplayData] = useState<{name: string; avatar: string} | null>(
    assistant ? { name: assistant.name, avatar: assistant.avatar } : null
  );

  // Check if we have pending assistant data from a duplication (client-side only)
  useEffect(() => {
    if (!assistant) {
      try {
        const pendingData = sessionStorage.getItem('pendingAssistantLoad');
        if (pendingData) {
          const parsed = JSON.parse(pendingData);
          // Only use if less than 5 seconds old (to avoid stale data)
          if (Date.now() - parsed.timestamp < 5000) {
            setDisplayData(parsed);
          } else {
            sessionStorage.removeItem('pendingAssistantLoad');
          }
        }
      } catch (e) {
        console.warn('Could not read pending assistant data:', e);
      }
    }
  }, [assistant]);

  return (
    <div
      className={`fixed inset-0 z-50 bg-gray-50 flex items-center justify-center p-4 ${
        isExiting ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
    >
      {/* Simple centered loading - faster, cleaner */}
      <div className="text-center space-y-6 max-w-md animate-scaleIn">
        {/* Avatar or generic spinner */}
        <div className="flex justify-center">
          {displayData ? (
            <div className="relative">
              <div className="h-24 w-24 rounded-full ring-4 ring-primary/20 overflow-hidden bg-muted">
                <img
                  src={displayData.avatar}
                  alt={displayData.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="absolute -bottom-1 -right-1">
                <div className="h-8 w-8 rounded-full border-4 border-white bg-primary flex items-center justify-center">
                  <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                </div>
              </div>
            </div>
          ) : (
            <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center ring-4 ring-primary/10">
              <div className="h-16 w-16 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
          )}
        </div>

        {/* Loading text */}
        <div className="space-y-2">
          {displayData ? (
            <>
              <h2 className="text-xl font-semibold text-foreground">
                {displayData.name}
              </h2>
              <p className="text-sm text-muted-foreground">
                Loading conversation...
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-foreground">
                Loading...
              </h2>
              <p className="text-sm text-muted-foreground">
                Preparing your assistant
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Helper function to map Assistant to ConfigProps
// Adjust this mapping based on exactly what SharePage needs
function mapAssistantToConfigProps(assistant: Assistant): ConfigProps {
  // Determine senderName - Placeholder logic: use Owner info if available, else default.
  // For now, we'll use ownerEmail as a stand-in or a default.
  const senderName = assistant.ownerEmail || "Assistant Owner"; // Example: Use email or default

  return {
    // --- From Assistant ---
    assistantId: assistant.assistantId,
    templateId: assistant.templateId,
    name: assistant.name,
    avatar: assistant.avatar,
    adminEmail: assistant.ownerEmail, // Map ownerEmail to adminEmail
    initialMessage: assistant.initialMessage || '',
    initialInstructions: assistant.initialInstructions || '',
    initialDocuments: assistant.initialDocuments || [], // Ensure it's an array
    tools: assistant.tools || [],
    toolConfigs: assistant.toolConfigs || {},
    accessControl: assistant.accessControl, // Pass access control rules

    // --- Needs specific logic or default ---
    senderName: senderName, // Adjust as needed

    // --- Not directly on Assistant, add if needed ---
    selectedItems: [], // Example default if SharePage uses this
  };
}

export default function DynamicAssistantPage({
  params: { assistantId }
}: {
  params: { assistantId: string }
}) {
  const [config, setConfig] = useState<ConfigProps | null>(null);
  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const [loadingState, setLoadingState] = useState<'loading' | 'exiting' | 'complete'>('loading');
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const configRef = useRef<ConfigProps | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadAssistant() {
      if (isMounted) {
          setConfig(null);
          setAssistant(null);
          setPermissionDenied(false);
          setFetchError(null);
          setLoadingState('loading');
      }
      try {
        // Fetch the assistant using the new method and assistantId
        //console.log(`Attempting to fetch assistant: ${assistantId}`);
        const fetchedAssistant = await FirebaseService.getAssistant(assistantId);

        if (!fetchedAssistant) {
          console.log(`Assistant not found: ${assistantId}`);
          if (isMounted) notFound();
          return;
        }
        //console.log("DynamicAssistantPage: Fetched Assistant Data:", fetchedAssistant);

        // Store the assistant for loading screen
        if (isMounted) {
          setAssistant(fetchedAssistant);
        }

        // Map the Assistant data to the ConfigProps format
        const configProps = mapAssistantToConfigProps(fetchedAssistant);
        //console.log("DynamicAssistantPage: Mapped ConfigProps:", configProps);

        if (isMounted && !configRef.current) {
          configRef.current = configProps;
          setConfig(configProps);

          // Clean up pending assistant data from sessionStorage
          try {
            sessionStorage.removeItem('pendingAssistantLoad');
          } catch (e) {
            console.warn('Could not clear pending assistant data:', e);
          }

          // Start exit animation
          setLoadingState('exiting');
          // Complete after animation
          setTimeout(() => {
            if (isMounted) {
              setLoadingState('complete');
            }
          }, 300); // Match animation duration
        }
      } catch (error: any) {
        // permission-denied should no longer happen here for logged-in users
        console.error(`Error loading assistant ${assistantId}:`, error);
        if (isMounted) {
          // Handle other errors (network, invalid data mapping, etc.)
          setFetchError("Failed to load assistant data.");
          setLoadingState('complete'); // Ensure loading completes
        }
      }
    }


    loadAssistant();

    return () => {
      isMounted = false;
    };
  }, [assistantId]); 

  // --- Memoization logic ---
  // It compares the current config state with the ref'd version
  const memoizedConfig = useMemo(() => {
    // If config state is null, return null
    if (!config) return null;

    // If the ref exists and stringified versions match, return the stable ref version
    if (
      configRef.current &&
      JSON.stringify(configRef.current) === JSON.stringify(config)
    ) {
      return configRef.current;
    }

    // If they don't match (or ref is null), update the ref with a deep copy
    // This is crucial for arrays like initialDocuments
    configRef.current = {
      ...config,
      initialDocuments: [...(config.initialDocuments || [])] // Ensure deep copy of documents array
    };

    // Return the newly updated ref version
    return configRef.current;
  }, [config]);

  // --- Rendering logic ---
  if (loadingState !== 'complete') {
    //console.log("Render: Loading state active.");
    return <SharePageLoading isExiting={loadingState === 'exiting'} assistant={assistant} />;
  }

  // **** ADDED: Check for generic fetch error state ****
  if (fetchError) {
    console.log("Render: Fetch Error.");
    // You might want a more specific error page component here
    return <ErrorPage message={fetchError} />; // Assuming ErrorPage component exists
  }

  // Check if config is loaded successfully *after* handling errors
  if (!memoizedConfig) {
    // This case might be hit if loading completes but config is still null
    console.log("Loading complete but memoizedConfig is null, rendering notFound.");
    notFound();
    return null; // Or return null explicitly after calling notFound()

  }

  // Pass the config to SharePage. SharePage should handle access control checks internally.
  //console.log("DynamicAssistantPage: Rendering SharePage with config:", memoizedConfig);

  return (
    <div className="animate-fadeIn">
            <div className="fixed inset-0 z-0 w-full h-full pointer-events-none">
            <Aurora
              colorStops={["#FF9966", "#FFD93D", "#FF6B6B"]}
              blend={0.9}                // Medium blend for better visibility
              amplitude={0.8}            // Higher amplitude for more noticeable waves
              speed={0.3}                // Slow animation speed
            />
            </div>
      <SharePage config={memoizedConfig} />
    </div>
  );
}